import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import StepConnector, { stepConnectorClasses, StepConnectorProps } from '@mui/material/StepConnector';
import type { StepIconProps } from '@mui/material/StepIcon';
import { ArrowDropDownIcon, DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  ChevronRightIcon,
  InfoIcon,
  LockKeyholeIcon,
  LockKeyholeOpenIcon,
  SaveIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import disparityDetailsBg from '../../assets/images/dashboard-hero.png';
import { useCalculatedDateRange } from '../../hooks/useCalculatedDateRange';
import { useProcessedRaces } from '../../hooks/useProcessedRaces';
import { useCaseApi } from '../../services/api/useCase.api';
import { useDisparityApi } from '../../services/api/useDisparity.api';
import { useDisparityStageApi } from '../../services/api/useDisparityStage.api';
import { useDisparityTypeApi } from '../../services/api/useDisparityType.api';
import { Case } from '../../types/Case/Case.type';
import { ComparisonPool } from '../../types/Disparity/ComparisonPool/ComparisonPool.type';
import { NewDisparity } from '../../types/Disparity/NewDisparity.type';
import { ComparisonPoolType } from '../../types/enums/ComparisonPoolType.enum';
import { DisparityCalculationStatus } from '../../types/enums/DisparityCalculationStatus.enum';
import { DisparityStage } from '../../types/enums/DisparityStage.enum';
import { DisparitySubType } from '../../types/enums/DisparitySubType.enum';
import { DisparityType } from '../../types/enums/DisparityType.enum';
import { getDisparityColor } from '../../utils/disparityColor';
import {
  isSameDayDate,
  isUnknownStartDate,
  resolveInitialStartDate,
  UNKNOWN_START_DATE,
} from '../../utils/disparityDates';
import ComparatorTool from '../ComparatorTool/ComparatorTool';
import ComparisonPoolsPreview from '../ComparatorTool/ComparisonPoolsPreview';
import TooltipPopper from '../Tour/TooltipPopper';
import ChartContainer from '../Vizualization/ChartContainer';
import { DisparityFieldsData } from './DisparityFieldsForm';

const StyledDisparityDetails = styled('div')<{ disparitySelected: boolean }>(({ disparitySelected }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: disparitySelected ? 'flex-start' : 'center',
  justifyContent: disparitySelected ? 'flex-start' : 'center',
  gap: '16px',
  width: '100%',
  padding: '16px',
  paddingTop: '0',
  borderRadius: '8px',
  boxShadow: disparitySelected ? '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' : 'none',
  background: disparitySelected ? '#fff' : 'transparent',
  border: disparitySelected ? '1px solid #cbd5e0' : 'none',
  overflowY: 'auto',
}));

const StyledSingleBox = styled(Box)({
  backgroundColor: '#f6f6f6',
  padding: '8px',
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
});

const StyledSingleBoxContent = styled(Box)({
  background: '#fff',
  padding: '16px',
  fontSize: '14px',
  fontWeight: 500,
  marginTop: '8px',
  borderRadius: '6px',
  boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
});

const StyledFormControl = styled(FormControl)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
});

const StyledLabel = styled('label')({
  fontSize: '14px',
  fontWeight: 600,
});

const StyledStepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean } }>(
  ({ ownerState }) => ({
    color: '#CBD5E0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
    borderRadius: '50%',

    '& .StepIndicatorIcon-circle': {
      width: 22,
      height: 22,
      borderRadius: '50%',
      backgroundColor: 'white',
      border: '6px solid currentColor',
    },
    ...(ownerState.active && {
      color: '#4C516D',
      borderRadius: '50%',
      outline: '2px solid currentColor',
      outlineOffset: '2px',
    }),
    ...(ownerState.completed && {
      backgroundColor: '#4C516D',
      border: '1px solid #4C516D',
      borderRadius: '50%',
      width: 22,
      height: 22,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .StepIndicatorIcon-circle': {
        width: 10,
        height: 10,
        backgroundColor: '#fff',
        borderRadius: '50%',
      },
    }),
  }),
);

function StepIndicatorIcon(props: StepIconProps) {
  const { active = false, completed = false, className } = props;
  return (
    <StyledStepIconRoot ownerState={{ active, completed }} className={className}>
      <div className='StepIndicatorIcon-circle' />
    </StyledStepIconRoot>
  );
}

interface CustomConnectorProps extends StepConnectorProps {
  isTransitionType: boolean;
}

const CustomConnector = styled(StepConnector, {
  shouldForwardProp: (prop) => prop !== 'isTransitionType',
})<CustomConnectorProps>(({ isTransitionType }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: isTransitionType ? 'currentColor' : '#CBD5E0',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#CBD5E0',
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

interface DisparityDetailsProps {
  caseData?: Case;
  disparityId?: number;
  onDisparityDelete: () => void;
  onDisparitySelect?: (disparityId: number) => void;
}

function DisparityDetails({ caseData, disparityId, onDisparityDelete, onDisparitySelect }: DisparityDetailsProps) {
  const queryClient = useQueryClient();
  const { getDisparityById, updateDisparity } = useDisparityApi();
  const { getDisparityTypes } = useDisparityTypeApi();
  const { getDisparityStages } = useDisparityStageApi();
  const { deleteDisparities, postDisparityForCase } = useCaseApi();

  const containerRef = useRef<HTMLDivElement>(null);

  const [openDeleteDisparityDialog, setOpenDeleteDisparityDialog] = useState(false);
  const [openSaveDisparityDialog, setOpenSaveDisparityDialog] = useState(false);
  const [saveDialogErrorMessage, setSaveDialogErrorMessage] = useState<string | null>(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(null);
  const [subtypeMenuAnchor, setSubtypeMenuAnchor] = useState<null | HTMLElement>(null);
  const [comparisonPoolsData, setComparisonPoolsData] = useState<NewDisparity['comparison_pools'] | undefined>(
    undefined,
  );
  const [tooltipAnchorEl, setTooltipAnchorEl] = useState<SVGSVGElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const [disparityData, setDisparityData] = useState({
    typeId: null as number | null,
    subtypeId: null as number | null,
    raceId: null as number | null,
    startDate: null as Date | null,
    endDate: null as Date | null,
    stageId: null as number | null,
  });

  // Checkbox reflects whether the start date is the unknown/beginning date (01/01/1900)
  const includeUnknownDates = isUnknownStartDate(disparityData.startDate);

  // Out-of-range / invalid date picker errors block saving.
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const hasDateError = !!startDateError || !!endDateError;

  const { data: disparity, isLoading: disparityLoading } = useQuery({
    queryKey: ['disparityDetails', disparityId],
    queryFn: () => getDisparityById(Number(disparityId)),
    enabled: !!disparityId,
    refetchInterval: (query) => {
      const disparity = query.state.data;

      const hasIncomplete =
        disparity?.calculation_status?.id !== DisparityCalculationStatus.COMPLETED ||
        disparity?.score === null ||
        isNaN(Number(disparity?.score)) ||
        (disparity?.score !== undefined && disparity.score <= 0);

      return hasIncomplete ? 5000 : false;
    },
  });

  const { data: disparityTypes } = useQuery({
    queryKey: ['disparityTypes'],
    queryFn: () => getDisparityTypes(),
  });

  const { data: disparityStages } = useQuery({
    queryKey: ['disparityStages'],
    queryFn: () => getDisparityStages(),
  });

  const { mutate: deleteDisparityMutation } = useMutation({
    mutationKey: ['deleteDisparities', caseData?.id],
    mutationFn: () => deleteDisparities(Number(caseData?.id), { ids: disparityId ? [disparityId] : [] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      onDisparityDelete();
    },
  });

  const { mutate: lockUnlockDisparity } = useMutation({
    mutationKey: ['lockUnlockDisparity', disparityId],
    mutationFn: ({ disparityId, is_locked }: { disparityId: number; is_locked: boolean }) =>
      updateDisparity(disparityId, { is_locked }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disparityDetails', disparityId], exact: false });
    },
  });

  const { mutate: patchDisparity, isPending: isSaving } = useMutation({
    mutationKey: ['patchDisparity', disparityId],
    mutationFn: ({ disparityId, disparityData }: { disparityId: number; disparityData: NewDisparity }) =>
      updateDisparity(disparityId, disparityData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['disparityDetails', disparityId], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseExportData'], exact: false });
      setOpenSaveDisparityDialog(false);
      setSaveDialogErrorMessage(null);
      setComparisonPoolsData(undefined);
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        setSaveDialogErrorMessage(error.message);
      } else {
        setSaveDialogErrorMessage('Failed to update disparity.');
      }
    },
  });

  const { mutate: createDisparity, isPending: isCreatingDisparity } = useMutation({
    mutationKey: ['postDisparityForCase'],
    mutationFn: ({ disparityData }: { disparityData: NewDisparity }) =>
      postDisparityForCase(Number(caseData?.id), disparityData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      setOpenSaveDisparityDialog(false);
      setSaveDialogErrorMessage(null);

      if (data?.id) {
        onDisparitySelect?.(data.id);
      }

      setDisparityData({
        typeId: disparity?.type.id ?? null,
        subtypeId: disparity?.subtype?.id ?? null,
        raceId: disparity?.race?.id ?? null,
        startDate: resolveInitialStartDate(disparity?.start_date, disparity?.min_found_date),
        endDate: disparity?.max_found_date
          ? parseISO(disparity.max_found_date)
          : disparity?.end_date
            ? parseISO(disparity.end_date)
            : null,
        stageId: disparity?.stage?.id ?? null,
      });

      setComparisonPoolsData(undefined);
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        setSaveDialogErrorMessage(error.message);
      } else {
        setSaveDialogErrorMessage('Failed to create disparity.');
      }
    },
  });

  const { endDate, minDate, maxDate } = useCalculatedDateRange({
    caseData: caseData,
    initialStartDate: disparity?.start_date ? parseISO(disparity.start_date) : null,
    initialEndDate: disparity?.end_date ? parseISO(disparity.end_date) : null,
  });

  const { processedRaces } = useProcessedRaces({ caseData, includeComparisonRaces: true });

  // start undefined so a remount with cached query data still syncs the form fields
  const [prevDisparity, setPrevDisparity] = useState<typeof disparity>(undefined);
  if (disparity !== prevDisparity) {
    setPrevDisparity(disparity);
    if (disparity) {
      setDisparityData({
        typeId: disparity.type.id,
        subtypeId: disparity.subtype?.id ?? null,
        raceId: disparity.race?.id ?? null,
        startDate: resolveInitialStartDate(disparity.start_date, disparity.min_found_date),
        endDate: disparity.max_found_date
          ? parseISO(disparity.max_found_date)
          : disparity.end_date
            ? parseISO(disparity.end_date)
            : null,
        stageId: disparity.stage?.id ?? null,
      });
    }
  }

  const disparityModified = useMemo(() => {
    if (!disparity) return false;

    const baselineStartDate = resolveInitialStartDate(disparity.start_date, disparity.min_found_date);
    const baselineEndDate = disparity.max_found_date ?? disparity.end_date;

    const isDisparityChanged =
      disparityData.typeId === disparity.type.id &&
      disparityData.subtypeId === (disparity.subtype?.id ?? null) &&
      disparityData.raceId === (disparity.race?.id ?? null) &&
      ((disparityData.startDate === null && !baselineStartDate) ||
        (disparityData.startDate &&
          baselineStartDate &&
          disparityData.startDate.getTime() === baselineStartDate.getTime())) &&
      ((disparityData.endDate === null && !baselineEndDate) ||
        (disparityData.endDate &&
          baselineEndDate &&
          disparityData.endDate.getTime() === parseISO(baselineEndDate).getTime())) &&
      disparityData.stageId === (disparity.stage?.id ?? null);

    return !isDisparityChanged;
  }, [disparityData, disparity]);

  const handleDisparityFieldChange =
    <K extends keyof typeof disparityData>(field: K) =>
    (value: (typeof disparityData)[K]) => {
      setDisparityData((prev) => ({
        ...prev,
        [field]: value,
      }));

      toggleTypeMenu();
    };

  const handleIncludeUnknownDatesToggle = (checked: boolean) => {
    const minFoundDate = disparity?.min_found_date;

    if (checked) {
      // Include unknown dates: switch the start date to the unknown/beginning date (1900-01-01)
      setDisparityData((prev) => ({ ...prev, startDate: UNKNOWN_START_DATE }));
    } else if (minFoundDate) {
      // Default to the first real found date, but keep any date the user has already entered
      setDisparityData((prev) =>
        isUnknownStartDate(prev.startDate) ? { ...prev, startDate: parseISO(minFoundDate) } : prev,
      );
    }
  };

  const isStageDisabled = (stageId?: number) => {
    if (stageId === DisparityStage.SENTENCING) return true;
    if (stageId === DisparityStage.ARREST && disparityData.subtypeId === DisparitySubType.ENHANCEMENT) return true;
    return false;
  };

  const handleStepClick = (index: number) => {
    const stage = disparityStages?.[index];
    if (stage && !isStageDisabled(stage.id)) {
      handleDisparityFieldChange('stageId')(stage.id);
    }
  };

  const handleDeleteDisparityDialogToggle = () => {
    setOpenDeleteDisparityDialog(!openDeleteDisparityDialog);
  };

  const handleSaveDisparityDialogToggle = () => {
    setOpenSaveDisparityDialog(!openSaveDisparityDialog);
  };

  const handleCancelSaveDialog = () => {
    // Reset disparity data to original values
    if (disparity) {
      setDisparityData({
        typeId: disparity.type.id,
        subtypeId: disparity.subtype?.id ?? null,
        raceId: disparity.race?.id ?? null,
        startDate: resolveInitialStartDate(disparity.start_date, disparity.min_found_date),
        endDate: disparity.max_found_date
          ? parseISO(disparity.max_found_date)
          : disparity.end_date
            ? parseISO(disparity.end_date)
            : null,
        stageId: disparity.stage?.id ?? null,
      });
    }
    setComparisonPoolsData(undefined);
    setSaveDialogErrorMessage(null);
    setOpenSaveDisparityDialog(false);
  };

  const handleDisparityDelete = () => {
    deleteDisparityMutation();
    setOpenDeleteDisparityDialog(false);
  };

  const handleApplyFromComparatorTool = (
    data: DisparityFieldsData & { comparisonPools: NewDisparity['comparison_pools'] },
  ) => {
    setDisparityData({
      typeId: data.typeId,
      subtypeId: data.subtypeId,
      raceId: data.raceId,
      startDate: data.startDate,
      endDate: data.endDate,
      stageId: data.stageId,
    });
    setComparisonPoolsData(data.comparisonPools);
    setOpenSaveDisparityDialog(true);
  };

  const handleTooltipToggle = (event: React.MouseEvent<Element>) => {
    if (event.currentTarget instanceof SVGSVGElement) {
      setTooltipAnchorEl(event.currentTarget);
    }
    setTooltipOpen(!tooltipOpen);
  };

  const handleTooltipClose = () => setTooltipOpen(false);

  const transformComparisonPools = (pools: ComparisonPool[]): NewDisparity['comparison_pools'] => {
    const comparisonPoolsFormat = {
      base: [] as number[][],
      inclusion: [] as number[][],
      exclusion: [] as number[][],
    };

    pools.forEach((pool) => {
      const ids = pool.comparison_pool_statutes.map((s) => s.statute_id);
      if (pool.comparison_pool_type.id === ComparisonPoolType.BASE) {
        comparisonPoolsFormat.base.push(ids);
      } else if (pool.comparison_pool_type.id === ComparisonPoolType.INCLUSION) {
        comparisonPoolsFormat.inclusion.push(ids);
      } else if (pool.comparison_pool_type.id === ComparisonPoolType.EXCLUSION) {
        comparisonPoolsFormat.exclusion.push(ids);
      }
    });

    return comparisonPoolsFormat;
  };

  const handleSaveDisparity = (action: 'new' | 'update') => {
    const poolPayload =
      comparisonPoolsData ??
      (disparity?.comparison_pools ? transformComparisonPools(disparity.comparison_pools) : undefined);

    const baseStatuteIds =
      poolPayload?.base?.[0] ??
      disparity?.comparison_pools
        .find((pool) => pool.comparison_pool_type.id === ComparisonPoolType.BASE)
        ?.comparison_pool_statutes.map((statute) => statute.statute_id) ??
      [];

    const payload: NewDisparity = {
      statutes_ids: baseStatuteIds,
      stage_id: disparityData.stageId ?? -1,
      type_id: disparityData.typeId ?? -1,
      ...(selectedType?.subtypes?.length &&
        disparityData.subtypeId !== null && { subtype_id: disparityData.subtypeId }),
      race_id: disparityData.raceId ?? -1,
      start_date: disparityData.startDate ? format(disparityData.startDate, 'yyyy-MM-dd') : '',
      end_date: disparityData.endDate ? format(disparityData.endDate, 'yyyy-MM-dd') : '',
      comparison_pools: poolPayload,
    };

    if (action === 'new') {
      createDisparity({ disparityData: payload });
    } else if (action === 'update' && disparityId && disparity) {
      patchDisparity({ disparityId, disparityData: payload });
    }
  };

  const initialDisparityStage = disparityStages?.findIndex((stage) => stage.id === disparity?.stage?.id) ?? 0;

  const completedSteps: boolean[] = (disparityStages ?? []).map((_, index) => {
    if (initialDisparityStage === -1) return false;
    if (disparity?.type?.id === DisparityType.TRANSITION) {
      return index === initialDisparityStage - 1;
    } else {
      return false;
    }
  });

  const getDisparityTypeLabel = (typeId?: number | null, subtypeId?: number | null) => {
    const type = disparityTypes?.find((t) => t.id === typeId);
    if (!type) return '';
    const subtype = type.subtypes?.find((s) => s.id === subtypeId);
    return subtype ? `${type.name} > ${subtype.name}` : type.name;
  };

  const selectedType = disparityTypes?.find((t) => t.id === disparityData.typeId);

  const toggleTypeMenu = (event?: React.MouseEvent<HTMLElement>) => {
    setTypeMenuAnchor((prev) => (prev ? null : event?.currentTarget || null));
    setSubtypeMenuAnchor(null);
  };

  const toggleSubtypeMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSubtypeMenuAnchor((prev) => (prev ? null : event?.currentTarget || null));
  };

  const disableWobbler = disparity?.comparison_pools
    .find((pool) => pool.comparison_pool_type.id === ComparisonPoolType.BASE)
    ?.comparison_pool_statutes.some((statute) => !statute.is_wobbler);

  // Reset comparisonPoolsData during render when disparityId changes
  const [prevDisparityId, setPrevDisparityId] = useState(disparityId);
  if (disparityId !== prevDisparityId) {
    setPrevDisparityId(disparityId);
    setComparisonPoolsData(undefined);
  }

  const isCalculatingDisparity =
    disparity?.calculation_status?.id !== DisparityCalculationStatus.COMPLETED &&
    disparity?.calculation_status?.id !== DisparityCalculationStatus.FAILED &&
    disparity?.calculation_status !== null;

  useEffect(() => {
    if (isCalculatingDisparity && containerRef.current) {
      containerRef.current.scrollTo({ top: 0 });
    }
  }, [isCalculatingDisparity]);

  if (!disparityId) {
    return (
      <StyledDisparityDetails disparitySelected={false} id='tour-disparity-details'>
        <img src={disparityDetailsBg} style={{ width: '300px' }} alt='Disparity details' />
        <p style={{ fontSize: '20px', fontWeight: 600, marginBottom: 0 }}>Case details</p>
        <p style={{ margin: 0 }}>Disparity not selected</p>
      </StyledDisparityDetails>
    );
  }

  if (disparityLoading) {
    return (
      <StyledDisparityDetails
        disparitySelected={true}
        sx={{ justifyContent: 'center', alignItems: 'center' }}
        id='tour-disparity-details'
      >
        <CircularProgress />
      </StyledDisparityDetails>
    );
  }

  return (
    <StyledDisparityDetails
      ref={containerRef}
      disparitySelected={true}
      id='tour-disparity-details'
      sx={{
        overflow: isCalculatingDisparity ? 'hidden' : 'auto',
      }}
    >
      {isCalculatingDisparity && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
            borderRadius: '6px',
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      <Box sx={{ width: '100%', position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff', paddingTop: '16px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant='h5' sx={{ fontSize: '30px', fontWeight: 700, marginBottom: '16px' }}>
            {disparity?.title}
          </Typography>
          <Box>
            <Button
              variant='contained'
              disableElevation
              color='primary'
              disabled={!disparityModified || isSaving || hasDateError}
              onClick={handleSaveDisparityDialogToggle}
              startIcon={<SaveIcon size={16} />}
              sx={{ marginRight: '16px', fontWeight: 600 }}
            >
              Save Changes
            </Button>
            <Dialog
              fullWidth
              maxWidth='xs'
              open={openSaveDisparityDialog}
              onClose={(_event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                  return;
                }
              }}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
            >
              <DialogTitle
                sx={{ fontSize: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}
              >
                Update disparity
                <Tooltip title='Cancel changes'>
                  <IconButton
                    aria-label='close-changes'
                    onClick={handleCancelSaveDialog}
                    disabled={isCreatingDisparity || isSaving}
                    sx={{ color: 'grey.500' }}
                  >
                    <XIcon size={20} />
                  </IconButton>
                </Tooltip>
              </DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ fontSize: '14px' }}>
                  Disparity has been modified. Update the selected disparity or save as a new one to keep the changes.
                </DialogContentText>
                {saveDialogErrorMessage && (
                  <Typography sx={{ color: 'error.main', mt: 2, fontSize: '14px', fontWeight: 500 }}>
                    {saveDialogErrorMessage}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleSaveDisparity('new')} disabled={isCreatingDisparity || isSaving}>
                  Save as new
                </Button>
                <Button onClick={() => handleSaveDisparity('update')} disabled={isCreatingDisparity || isSaving}>
                  Update existing
                </Button>
              </DialogActions>
            </Dialog>
            {disparity?.is_locked ? (
              <Tooltip title='Unlock Disparity'>
                <IconButton
                  color='accentBlue'
                  onClick={() => lockUnlockDisparity({ disparityId: disparity.id, is_locked: false })}
                >
                  <LockKeyholeIcon size={22} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title='Lock Disparity'>
                <IconButton
                  color='accentBlue'
                  onClick={() => {
                    if (disparity) {
                      lockUnlockDisparity({ disparityId: disparity.id, is_locked: true });
                    }
                  }}
                >
                  <LockKeyholeOpenIcon size={22} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Delete Disparity'>
              <IconButton color='accentBlue' onClick={handleDeleteDisparityDialogToggle}>
                <Trash2Icon size={22} />
              </IconButton>
            </Tooltip>
            <Dialog
              fullWidth
              maxWidth='xs'
              open={openDeleteDisparityDialog}
              onClose={handleDeleteDisparityDialogToggle}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
            >
              <DialogTitle sx={{ fontSize: '16px' }}>Delete disparity</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ fontSize: '14px' }}>
                  Are you sure you want to delete this disparity? This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteDisparityDialogToggle}>Cancel</Button>
                <Button onClick={handleDisparityDelete} color='error'>
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: '28px',
            marginTop: '28px',
          }}
        >
          <Box>
            <Typography variant='body2' sx={{ color: '#858585', fontWeight: 600, letterSpacing: '0.04em' }}>
              STATUTE
            </Typography>
            <Typography variant='body2' sx={{ fontSize: '14px', color: '#2d3748', fontWeight: 600 }}>
              {disparity?.subtitle}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body2' sx={{ color: '#858585', fontWeight: 600, letterSpacing: '0.04em' }}>
              DISPARITY TYPE
            </Typography>
            <Typography variant='body2' sx={{ fontSize: '14px', color: '#2d3748', fontWeight: 600 }}>
              {getDisparityTypeLabel(disparity?.type?.id ?? undefined, disparity?.subtype?.id ?? undefined)}
            </Typography>
          </Box>
          <div id='tour-comparator-button'>
            <ComparatorTool
              existingDisparity={disparity}
              countyId={disparity?.case.county.id}
              caseData={caseData}
              disparityStages={disparityStages}
              disparityTypes={disparityTypes}
              initialDisparityFields={{
                statuteIds:
                  disparity?.comparison_pools
                    .find((pool) => pool.comparison_pool_type.id === ComparisonPoolType.BASE)
                    ?.comparison_pool_statutes.map((s) => s.statute_id) ?? [],
                stageId: disparityData.stageId,
                typeId: disparityData.typeId,
                subtypeId: disparityData.subtypeId,
                raceId: disparityData.raceId,
                startDate: disparityData.startDate,
                endDate: disparityData.endDate,
              }}
              initialComparisonPools={
                disparity?.comparison_pools
                  ? {
                      base: [
                        disparity.comparison_pools
                          .find((pool) => pool.comparison_pool_type.id === ComparisonPoolType.BASE)
                          ?.comparison_pool_statutes.map((s) => s.statute_id) ?? [],
                      ],
                      inclusion: disparity.comparison_pools
                        .filter((pool) => pool.comparison_pool_type.id === ComparisonPoolType.INCLUSION)
                        .map((pool) => pool.comparison_pool_statutes.map((s) => s.statute_id)),
                      exclusion: disparity.comparison_pools
                        .filter((pool) => pool.comparison_pool_type.id === ComparisonPoolType.EXCLUSION)
                        .map((pool) => pool.comparison_pool_statutes.map((s) => s.statute_id)),
                    }
                  : undefined
              }
              onApply={handleApplyFromComparatorTool}
            />
          </div>
        </Box>
        <ComparisonPoolsPreview pools={comparisonPoolsData} disparity={comparisonPoolsData ? undefined : disparity} />
        <Divider sx={{ width: '100%', paddingTop: '16px' }} />
      </Box>
      <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <StyledSingleBox>
          <Typography variant='body1' sx={{ fontWeight: 600, marginLeft: '8px' }}>
            Overview
          </Typography>
          <StyledSingleBoxContent>
            {disparity?.disparity_overview && (
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {disparity?.disparity_overview?.replace('Non-client', `Non-${disparity.case.client_race.name}`)}
              </Typography>
            )}
          </StyledSingleBoxContent>
        </StyledSingleBox>
        <StyledSingleBox>
          <Typography variant='body1' sx={{ fontWeight: 600, marginLeft: '8px' }}>
            Disparity Score
          </Typography>
          <StyledSingleBoxContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: '40px',
                justifyContent: 'space-between',
              }}
            >
              {disparity?.score_description && (
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {disparity?.score_description?.replace('Non-client', `Non-${disparity.case.client_race.name}`)}
                </Typography>
              )}
              <Typography
                variant='h2'
                sx={{
                  color: getDisparityColor(Number(disparity?.score)),
                  fontWeight: 700,
                }}
              >
                {disparity?.score ? disparity.score.toFixed(1) : 'N/A'}
              </Typography>
            </Box>
          </StyledSingleBoxContent>
        </StyledSingleBox>
      </Box>

      <StyledSingleBox sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { md: 'column', lg: 'row' },
            alignItems: { md: 'flex-start', lg: 'center' },
            justifyContent: 'space-between',
            gap: '16px',
            marginBottom: '8px',
            width: '100%',
            marginTop: '10px',
          }}
        >
          <Typography variant='body1' sx={{ fontWeight: 600, marginLeft: '8px' }}>
            Disparity Detail
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={disparity?.is_locked}
                  value={disparityData.startDate}
                  label={
                    isSameDayDate(disparityData.startDate, disparity?.min_found_date)
                      ? 'Earliest found date'
                      : undefined
                  }
                  minDate={minDate ?? undefined}
                  maxDate={endDate ?? maxDate ?? undefined}
                  onChange={handleDisparityFieldChange('startDate')}
                  onError={(error) => setStartDateError(error)}
                  slotProps={{
                    textField: {
                      slotProps: {
                        input: {
                          sx: {
                            fontSize: '14px',
                            backgroundColor: '#fff',
                          },
                        },
                        inputLabel: {
                          sx: {
                            fontSize: '14px',
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
              <FormControlLabel
                sx={{ m: 0 }}
                disabled={disparity?.is_locked || !disparity?.min_found_date}
                control={
                  <Checkbox
                    size='small'
                    sx={{ py: 0 }}
                    checked={includeUnknownDates}
                    onChange={(event) => handleIncludeUnknownDatesToggle(event.target.checked)}
                    disableRipple
                  />
                }
                label={<Typography sx={{ fontSize: '12px' }}>Include unknown dates</Typography>}
              />
            </Box>
            <Typography sx={{ mt: '12px' }}>-</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                disabled={disparity?.is_locked}
                value={disparityData.endDate}
                minDate={disparityData.startDate ?? minDate ?? undefined}
                onChange={handleDisparityFieldChange('endDate')}
                onError={(error) => setEndDateError(error)}
                slotProps={{
                  textField: {
                    slotProps: {
                      input: {
                        sx: {
                          fontSize: '14px',
                          backgroundColor: '#fff',
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
        <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <StyledSingleBoxContent>
            <Button
              onClick={toggleTypeMenu}
              variant='outlined'
              disabled={disparity?.is_locked}
              sx={{
                padding: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                {getDisparityTypeLabel(disparityData?.typeId, disparityData?.subtypeId)}
              </Typography>
              <ArrowDropDownIcon />
            </Button>
            <Menu
              anchorEl={typeMenuAnchor}
              open={Boolean(typeMenuAnchor)}
              onClose={() => toggleTypeMenu()}
              slotProps={{ paper: { sx: { minWidth: 250 } } }}
            >
              {disparityTypes?.map((type) =>
                type.subtypes.length > 0 ? (
                  <MenuItem
                    key={type.id}
                    onClick={toggleSubtypeMenu}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {type.name}
                    <ChevronRightIcon size={18} />
                    <Menu
                      anchorEl={subtypeMenuAnchor}
                      open={Boolean(subtypeMenuAnchor)}
                      onClose={() => toggleSubtypeMenu({} as React.MouseEvent<HTMLElement>)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      slotProps={{ paper: { sx: { minWidth: 250 } } }}
                    >
                      {type.subtypes.map((subtype) => (
                        <MenuItem
                          key={subtype.id}
                          sx={{
                            padding: '8px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                          onClick={() => {
                            handleDisparityFieldChange('typeId')(type.id);
                            handleDisparityFieldChange('subtypeId')(subtype.id);
                            toggleTypeMenu();
                          }}
                          disabled={
                            (subtype.id === DisparitySubType.WOBBLERS && disableWobbler) ||
                            (subtype.id === DisparitySubType.TARGET_VERSUS_COMPARATORS &&
                              !(
                                (comparisonPoolsData?.inclusion && comparisonPoolsData.inclusion.length > 0) ||
                                (disparity?.comparison_pools &&
                                  disparity.comparison_pools.some(
                                    (pool) =>
                                      pool.comparison_pool_type.id === ComparisonPoolType.INCLUSION &&
                                      pool.comparison_pool_statutes.length > 0,
                                  ))
                              ))
                          }
                        >
                          {subtype.name}
                          {disparityData.typeId === DisparityType.SEVERITY &&
                            disparityData.subtypeId === subtype.id && (
                              <Checkbox size='small' sx={{ padding: 0 }} checked={true} />
                            )}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MenuItem>
                ) : (
                  <MenuItem
                    key={type.id}
                    onClick={() => {
                      handleDisparityFieldChange('typeId')(type.id);
                      handleDisparityFieldChange('subtypeId')(null);
                      toggleTypeMenu();
                    }}
                    selected={disparityData.typeId === type.id}
                  >
                    {type.name}
                  </MenuItem>
                ),
              )}
            </Menu>
            <Box sx={{ marginTop: '16px' }}>
              {disparity?.type?.description ? (
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {disparity.type.description}
                </Typography>
              ) : disparity?.subtype?.description ? (
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {disparity.subtype.description}
                </Typography>
              ) : null}
            </Box>
          </StyledSingleBoxContent>
          <StyledSingleBoxContent>
            <InfoIcon
              size={20}
              color='#6B7280'
              strokeWidth={2.5}
              style={{ cursor: 'pointer', alignSelf: 'flex-end' }}
              onClick={handleTooltipToggle}
            />
            <TooltipPopper
              open={tooltipOpen}
              anchorEl={tooltipAnchorEl}
              onClose={handleTooltipClose}
              placement='left-end'
              title='Transition graph'
              description='A case is where you can identify, specify, and store evidence of disparities related to a particular set of facts or details.'
            />
            <Stepper
              sx={{
                border: '1px solid #BDBDBD',
                borderRadius: '8px',
                padding: '16px',
                paddingTop: '24px',
                pointerEvents: disparity?.is_locked ? 'none' : 'auto',
                marginTop: 2,
              }}
              alternativeLabel
              connector={<CustomConnector isTransitionType={disparity?.type.id === DisparityType.TRANSITION} />}
              activeStep={disparityStages?.findIndex((stage) => stage.id === disparityData.stageId) ?? 0}
            >
              {disparityStages &&
                disparityStages.map((label, index) => {
                  const stageDisabled = isStageDisabled(disparityStages[index]?.id);
                  return (
                    <Step
                      key={typeof label === 'string' ? label : label.name}
                      completed={completedSteps[index]}
                      disabled={stageDisabled}
                    >
                      <StepLabel
                        onClick={stageDisabled ? undefined : () => handleStepClick(index)}
                        slots={{
                          stepIcon: StepIndicatorIcon,
                        }}
                        sx={{
                          opacity: disparity?.is_locked || stageDisabled ? 0.5 : 1,
                          cursor: stageDisabled ? 'not-allowed !important' : 'pointer',
                          '.MuiStepLabel-label': {
                            fontSize: '14px',
                            fontWeight: disparityData.stageId === disparityStages[index]?.id ? 600 : 500,
                            color: disparityData.stageId === disparityStages[index]?.id ? '#4C516D' : '#858585',
                          },
                        }}
                      >
                        {typeof label === 'string' ? label : label.name}
                      </StepLabel>
                    </Step>
                  );
                })}
            </Stepper>
            {disparity?.stage && (
              <Typography variant='body2' sx={{ fontWeight: 500, marginTop: '16px' }}>
                {disparity.stage.description}
              </Typography>
            )}
          </StyledSingleBoxContent>
        </Box>
      </StyledSingleBox>

      <Box sx={{ width: '100%' }}>
        <StyledSingleBox>
          <StyledFormControl sx={{ alignSelf: 'end' }}>
            <StyledLabel>Race Comparison</StyledLabel>
            <Select
              disabled={disparity?.is_locked}
              sx={{
                backgroundColor: '#fff',
                minWidth: '160px',
              }}
              value={disparityData.raceId || ''}
              onChange={(e) => handleDisparityFieldChange('raceId')(e.target.value as number)}
            >
              {processedRaces?.map((race) => (
                <MenuItem key={race.id} value={race.id}>
                  {race.name}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
          <StyledSingleBoxContent>
            {disparity?.comparison_numerator ? (
              <ChartContainer disparityData={disparity} />
            ) : (
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                No comparison data available for this disparity.
              </Typography>
            )}
          </StyledSingleBoxContent>
        </StyledSingleBox>
      </Box>
    </StyledDisparityDetails>
  );
}

export default DisparityDetails;
